import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, PackageOpen, Phone, Mail } from "lucide-react";
import { COMPANY } from "@/lib/api";
import TransparentImage from "@/components/TransparentImage";

const TYPE_LABEL = {
  "starter-motor": "Starter Motors",
  "alternator": "Alternators",
};

const SEGMENT_LABEL = {
  hgv: "HGV",
  bus: "Bus / PSV",
  industrial: "Industrial",
  marine: "Marine",
};

// Per-page marketing copy. Add entries here as the user provides them.
const PAGE_COPY = {
  "starter-motor:hgv": {
    heading: "HGV Starter Motors",
    image: "https://customer-assets.emergentagent.com/job_motor-ignite/artifacts/nrq4iof6_image.png",
    imageCaption: "Heavy-duty HGV starter motor · in-house stock",
    body: [
      "Want to find the best quality Starter motors for HGVs? Washington Rotating Electrics can provide starter motors for all makes and models of HGV.",
      "All we need is your vehicle registration to find the correct part needed for your vehicle. We have 100's of starter motors to choose from, flexible to your budget.",
      "If you need a starter motor for your HGV, contact us now for a quick and easy hassle-free quote at our best price.",
    ],
  },
  "starter-motor:bus": {
    heading: "PSV Coach/Bus",
    image: "https://customer-assets.emergentagent.com/job_motor-ignite/artifacts/u91aiwhf_image.png",
    imageCaption: "Heavy-duty PSV / Coach starter motor · in-house stock",
    body: [
      "Want to find the best quality Starter motors? Washington Rotating Electrics can provide starter motors for all makes and models of Bus/PSV Coach.",
      "All we need is your vehicle registration to find the correct part needed for your vehicle. We have 100's of starter motors to choose from, flexible to your budget.",
      "If you need a starter motor for your Bus/PSV Coach, contact us now for a quick and easy hassle-free quote at our best price.",
    ],
  },
  "starter-motor:industrial": {
    heading: "Industrial Starter Motors",
    image: "https://customer-assets.emergentagent.com/job_motor-ignite/artifacts/khdhgx8d_image.png",
    imageCaption: "Compact industrial starter motor · in-house stock",
    body: [
      "Want to find the best quality Starter motors? Washington Rotating Electrics can provide starter motors for all makes and models of Industrial machines and Vehicles.",
      "All we need is your vehicle/engine details to supply the correct part needed for your vehicle. We have 100's of starter motors to choose from.",
      "If you need a starter motor for your vehicle or machine, contact us now for a quick and easy hassle-free quote at our best price.",
    ],
  },
  "starter-motor:marine": {
    heading: "Marine Starter Motors",
    image: "https://customer-assets.emergentagent.com/job_motor-ignite/artifacts/z5tgbixy_image.png",
    imageCaption: "Marine engine starter motor · in-house stock",
    body: [
      "Want too find the best quality Starter motors? Washington Rotating Electrics can provide starter motors for all makes and models of Marine engines.",
      "All we need is your engine details to supply the correct part needed for your engine. We have 100's of starter motors too choose from flexible too your budget.",
      "If you need a starter motor for your vehicle or machine, contact us now for a quick and easy hassle-free quote at our best price.",
    ],
  },
  "alternator:hgv": {
    heading: "HGV Alternators",
    image: "https://customer-assets.emergentagent.com/job_motor-ignite/artifacts/rl0xygto_image.png",
    imageCaption: "Commercial / HGV alternator · in-house stock",
    body: [
      "At Washington Rotating Electrics we offer reliable and quality alternators for all makes and models of commercial and HGV vehicles.",
      "All we need is your vehicle details so we can supply you with the correct alternator for your vehicle.",
      "Please contact us for a hassle free quote.",
    ],
  },
  "alternator:bus": {
    heading: "PSV Coach/Bus Alternators",
    image: "https://customer-assets.emergentagent.com/job_motor-ignite/artifacts/kwh6oxtp_image.png",
    imageCaption: "Heavy-duty PSV / Coach alternator · in-house stock",
    body: [
      "At Washington Rotating Electrics we offer reliable and quality alternators for all makes and models of BUS and PSV coaches.",
      "All we need is your vehicle details so we can supply you with the correct alternator for your vehicle.",
      "Please contact us for a hassle free quote.",
    ],
  },
  "alternator:marine": {
    heading: "Marine Alternators",
    image: "https://customer-assets.emergentagent.com/job_motor-ignite/artifacts/b3qh51v4_image.png",
    imageCaption: "Marine engine alternator · in-house stock",
    body: [
      "At Washington Rotating Electrics we offer reliable and quality alternators for all makes and models of marine engines.",
      "All we need is the engine details so we can supply the correct alternator for your needs.",
      "Please contact us for a hassle free quote.",
    ],
  },
  "alternator:industrial": {
    heading: "Industrial Alternators",
    image: "https://customer-assets.emergentagent.com/job_motor-ignite/artifacts/qfvhu65w_image.png",
    imageCaption: "Industrial alternator · in-house stock",
    body: [
      "At Washington Rotating Electrics we offer reliable and quality alternators for all makes and models of industrial engines and machines.",
      "All we need is your engine or vehicle details so we can supply the correct alternator for you.",
      "Please contact us for a hassle free quote.",
    ],
  },
};

export default function Catalog() {
  const [params] = useSearchParams();
  const type = params.get("type");
  const segment = params.get("segment");

  const typeLabel = TYPE_LABEL[type];
  const segmentLabel = SEGMENT_LABEL[segment];

  const key = `${type}:${segment}`;
  const copy = PAGE_COPY[key];

  const heading =
    copy?.heading ||
    (segmentLabel && typeLabel
      ? `${segmentLabel} ${typeLabel}`
      : typeLabel || "Our parts catalog.");

  return (
    <main data-testid="catalog-page" className="min-h-[80vh]">
      {/* HERO */}
      <section className="border-b border-ink-600 bg-ink-800">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-8 md:py-12">
          <div className="font-mono-tech text-[10px] text-copper mb-3">
            / CATALOG {type ? `· ${type.toUpperCase().replace("-", " ")}` : ""}
            {segment ? ` · ${segment.toUpperCase()}` : ""}
          </div>
          <h1
            data-testid="catalog-heading"
            className="font-display text-3xl md:text-4xl leading-[1.05] max-w-3xl"
          >
            <span className="chrome-text">{heading}</span>
          </h1>
        </div>
      </section>

      {/* CONTENT */}
      {copy ? (
        <section className="border-b border-ink-600">
          <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
            <div
              data-testid="catalog-body-copy"
              className="md:col-span-7 space-y-6 text-steel-200 text-lg leading-relaxed"
            >
              {copy.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}

              <div className="pt-6 flex flex-wrap gap-4">
                <Link
                  to={`/quote?part=${type}`}
                  data-testid="catalog-cta-quote"
                  className="btn-copper"
                >
                  Get a Quote <ArrowRight size={14} />
                </Link>
                <Link to="/contact" className="btn-ghost">
                  Contact Us
                </Link>
              </div>
            </div>

            <aside className="md:col-span-5 flex flex-col gap-6 self-start">
              {copy.image && (
                <figure
                  data-testid="catalog-product-image-wrap"
                  className="relative"
                >
                  <div className="absolute inset-0 -z-10 opacity-40 blur-3xl bg-gradient-radial from-copper/30 via-transparent to-transparent" />
                  <TransparentImage
                    src={copy.image}
                    alt={copy.heading}
                    className="w-full h-auto max-h-[420px] object-contain drop-shadow-[0_18px_28px_rgba(0,0,0,0.6)]"
                    data-testid="catalog-product-image"
                  />
                  {copy.imageCaption && (
                    <figcaption className="mt-3 font-mono-tech text-[10px] text-steel-400 text-center">
                      ⏵ {copy.imageCaption}
                    </figcaption>
                  )}
                </figure>
              )}
              <div className="border border-ink-600 bg-ink-800 p-8">
              <div className="font-mono-tech text-[10px] text-copper mb-5">
                FAST QUOTE — JUST SEND YOUR VRN
              </div>
              <ul className="space-y-5 mb-8">
                <li className="flex gap-4">
                  <span className="font-display text-copper text-2xl shrink-0 w-8">01</span>
                  <div className="text-sm text-steel-200">
                    Send your vehicle registration number — that&apos;s all we need to match the right part.
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="font-display text-copper text-2xl shrink-0 w-8">02</span>
                  <div className="text-sm text-steel-200">
                    We come back with a fixed price within one working hour during opening times.
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="font-display text-copper text-2xl shrink-0 w-8">03</span>
                  <div className="text-sm text-steel-200">
                    Pay, then we dispatch — UK-wide.
                  </div>
                </li>
              </ul>
              <div className="border-t border-ink-600 pt-5 space-y-3">
                <a
                  href={`tel:${COMPANY.phone.replace(/\s+/g, "")}`}
                  className="flex items-center gap-3 text-sm hover:text-copper"
                  data-testid="catalog-sidebar-phone"
                >
                  <Phone size={14} className="text-copper" /> {COMPANY.phone}
                </a>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="flex items-center gap-3 text-sm hover:text-copper break-all"
                >
                  <Mail size={14} className="text-copper" /> {COMPANY.email}
                </a>
              </div>
              </div>
            </aside>
          </div>
        </section>
      ) : (
        <section className="border-b border-ink-600">
          <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-24 md:py-32">
            <div
              data-testid="catalog-empty-state"
              className="border border-ink-600 bg-ink-800 p-10 md:p-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center"
            >
              <div className="md:col-span-3 flex justify-center md:justify-start">
                <span className="grid place-items-center w-28 h-28 border border-ink-600 bg-ink-900">
                  <PackageOpen size={44} strokeWidth={1.4} className="text-copper" />
                </span>
              </div>

              <div className="md:col-span-9">
                <div className="font-mono-tech text-[10px] text-steel-400 mb-3">
                  / STATUS — IN PROGRESS
                </div>
                <h2 className="font-display text-3xl md:text-5xl leading-[0.95] mb-6">
                  {segmentLabel && typeLabel
                    ? `${segmentLabel} ${typeLabel.toLowerCase()} — stock list incoming.`
                    : "The catalog is being built."}
                </h2>
                <p className="text-steel-300 max-w-xl mb-8 leading-relaxed">
                  We&apos;re loading the shelves. In the meantime, send us a quote
                  request with your vehicle details and we&apos;ll match the right
                  part from stock.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to={`/quote${type ? `?part=${type}` : ""}`}
                    data-testid="catalog-quote-cta"
                    className="btn-copper"
                  >
                    Request a Quote <ArrowRight size={14} />
                  </Link>
                  <Link to="/contact" className="btn-ghost">
                    Get in Touch
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
